import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, TrendingUp } from 'lucide-react';
import { services, testimonials, stats } from '../mock';
import * as LucideIcons from 'lucide-react';

export const Home = () => {
  const getIcon = (iconName) => {
    const IconComponent = LucideIcons[iconName.charAt(0).toUpperCase() + iconName.slice(1)];
    return IconComponent ? <IconComponent size={32} /> : null;
  };

  return (
    <div className="min-h-screen" style={{ background: '#ECEC75' }}>
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 
            className="text-5xl md:text-7xl font-bold text-black mb-6"
            style={{ fontFamily: 'Crimson Text, serif' }}
          >
            Grow Your Brand with<br />Digital Excellence
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 mb-8 max-w-3xl mx-auto">
            Transform your business with data-driven digital marketing strategies 
            that deliver real, measurable results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/contact">
              <button className="bg-black text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-800 transition-all hover:translate-y-[-2px] shadow-lg flex items-center gap-2">
                Get Started Today
                <ArrowRight size={20} />
              </button>
            </Link>
            <Link to="/services">
              <button className="bg-transparent text-black border-2 border-black px-8 py-4 rounded-lg font-semibold text-lg hover:bg-black hover:text-white transition-all">
                View Our Services
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ background: '#e6e67c' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-12 md:gap-16">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-black mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>
                  {stat.value}
                </div>
                <div className="text-gray-700 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold text-black mb-4"
              style={{ fontFamily: 'Crimson Text, serif' }}
            >
              Our Services
            </h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Comprehensive digital marketing solutions tailored to your business goals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <div 
                key={service.id}
                className="bg-white p-8 rounded-xl hover:shadow-xl transition-all hover:translate-y-[-4px]"
                style={{ background: '#e6e67c' }}
              >
                <div className="text-black mb-4">
                  {getIcon(service.icon)}
                </div>
                <h3 className="text-2xl font-bold text-black mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                  {service.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <button className="bg-black text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-800 transition-all hover:translate-y-[-2px] shadow-lg">
                Explore All Services
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ background: '#e6e67c' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 
                className="text-4xl md:text-5xl font-bold text-black mb-6"
                style={{ fontFamily: 'Crimson Text, serif' }}
              >
                Why Choose Netex Digital?
              </h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                We're not just another marketing agency. We're your growth partner, 
                committed to delivering strategies that work and results that matter.
              </p>
              <ul className="space-y-4">
                {[
                  'Data-driven strategies with measurable ROI',
                  'Experienced team of digital marketing experts',
                  'Custom solutions tailored to your business',
                  'Transparent reporting and communication',
                  'Proven track record of success'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="text-black flex-shrink-0 mt-1" size={24} />
                    <span className="text-gray-700 text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <TrendingUp size={64} className="text-black mb-6" />
              <h3 className="text-3xl font-bold text-black mb-4" style={{ fontFamily: 'Crimson Text, serif' }}>
                Ready to Scale Your Business?
              </h3>
              <p className="text-gray-700 text-lg mb-6">
                Let's discuss how we can help you achieve your digital marketing goals. 
                Schedule a free consultation today.
              </p>
              <Link to="/contact">
                <button className="w-full bg-black text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-all">
                  Schedule Consultation
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold text-black mb-4"
              style={{ fontFamily: 'Crimson Text, serif' }}
            >
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-700">
              Real results from real businesses
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div 
                key={testimonial.id}
                className="p-8 rounded-xl shadow-md"
                style={{ background: '#e6e67c' }}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-black text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div>
                  <div className="font-bold text-black">{testimonial.name}</div>
                  <div className="text-gray-600">{testimonial.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ background: '#e6e67c' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl md:text-5xl font-bold text-black mb-6"
            style={{ fontFamily: 'Crimson Text, serif' }}
          >
            Ready to Transform Your Digital Presence?
          </h2>
          <p className="text-xl text-gray-700 mb-8">
            Join hundreds of businesses that trust Netex Digital to grow their brands
          </p>
          <Link to="/contact">
            <button className="bg-black text-white px-10 py-4 rounded-lg font-semibold text-lg hover:bg-gray-800 transition-all hover:translate-y-[-2px] shadow-lg">
              Let's Get Started
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};